// Звук нового сообщения.
//
// Прежний был две голые синусоиды, 880 и 1100 Гц, со сменой ноты встык и без
// плавного начала: пищал и щёлкал на стыках. Этот — один мягкий звук той же
// высоты, с которой начинался прежний (ля пятой октавы). Основной тон и пара
// слабых обертонов, которые гаснут быстрее него, — так звучит деревянный
// молоточек, а не пищалка. Атака в несколько миллисекунд и экспоненциальное
// затухание — без щелчков на краях.
//
// Собирается в WAV один раз и проигрывается через <audio>: файл, а не Web Audio,
// чтобы звук не зависел от того, разрешил ли браузер AudioContext.

const NOTES = [
  { at: 0, freq: 880, gain: 1 } // ля5
];
// Обертоны: кратность частоты, громкость относительно основного тона и время
// затухания (с). Высокие гаснут быстрее — звук сразу становится круглым.
const PARTIALS = [
  { mult: 1, amp: 1, decay: 0.2 },
  { mult: 2, amp: 0.2, decay: 0.11 },
  { mult: 3, amp: 0.06, decay: 0.06 }
];
const ATTACK_SEC = 0.008;
const DURATION_SEC = 0.6;
const TAIL_FADE_SEC = 0.04;
// Итоговая громкость — здесь, а не в audio.volume: так файл звучит ровно как в
// панели. Подобрана по средней громкости (RMS) к прежнему звуку с его volume
// 0.5: новый примерно на 1 дБ тише — мягче, но не теряется.
const PEAK = 0.075;

const synthesize = (sampleRate) => {
  const total = Math.floor(sampleRate * DURATION_SEC);
  const data = new Float32Array(total);
  for (const note of NOTES) {
    const start = Math.floor(note.at * sampleRate);
    for (let i = start; i < total; i += 1) {
      const t = (i - start) / sampleRate;
      const attack = t < ATTACK_SEC ? 0.5 - 0.5 * Math.cos((Math.PI * t) / ATTACK_SEC) : 1;
      let sample = 0;
      for (const partial of PARTIALS) {
        sample += partial.amp
          * Math.exp(-t / partial.decay)
          * Math.sin(2 * Math.PI * note.freq * partial.mult * t);
      }
      data[i] += note.gain * attack * sample;
    }
  }
  let peak = 0;
  for (let i = 0; i < total; i += 1) peak = Math.max(peak, Math.abs(data[i]));
  const scale = peak > 0 ? PEAK / peak : 0;
  const fadeFrom = total - Math.floor(TAIL_FADE_SEC * sampleRate);
  for (let i = 0; i < total; i += 1) {
    const fade = i >= fadeFrom ? (total - i) / (total - fadeFrom) : 1;
    data[i] *= scale * fade;
  }
  return data;
};

// 16-битный моно WAV. Возвращает ArrayBuffer — для Blob в браузере и для
// записи в файл при прослушивании вне панели.
export const buildMessageChimeWav = ({ sampleRate = 44100 } = {}) => {
  const samples = synthesize(sampleRate);
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeStr = (offset, text) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i += 1) {
    const value = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, Math.round(value * 32767), true);
  }
  return buffer;
};

// Компьютер — основной указатель мышь или тачпад. Телефоны и планшеты (палец)
// — без звука, как и написано в настройке. Узкое окно браузера на компьютере
// звук не выключает: решает устройство, а не ширина.
export const isDesktopDevice = () => {
  if (typeof window === "undefined") return false;
  if (typeof navigator !== "undefined" && navigator.userAgentData?.mobile) return false;
  try {
    return window.matchMedia("(pointer: fine)").matches;
  } catch (_) {
    return true;
  }
};
